/**
 * Belvoir Judge.me mobile reviews: stable swipe track with progressive loading.
 *
 * Judge.me can replace its native review list on each pagination click. The
 * visible track is an independent, persistent DOM sibling so swiping never
 * jumps to card one when Judge.me replaces page five with page six.
 *
 * Source of truth: the original Judge.me widget. Fail open to native paging.
 */
(() => {
  if (window.BelvoirJudgeMeStableCarousel) return;
  window.BelvoirJudgeMeStableCarousel = true;

  const MOBILE = window.matchMedia('(max-width: 749px)');
  const SECTION = '.bv-product-reviews[data-bv-local-mobile-layout="carousel"]';
  const TIMEOUT = 4500;
  const MAX_PAGES = 40;
  const mounted = new WeakMap();

  const nativeItems = section => [...section.querySelectorAll(
    '.jdgm-widget .jdgm-rev-widg__reviews > .jdgm-rev'
  )];
  const keyOf = element => element?.getAttribute('data-review-id') || '';

  function maxPage(section) {
    const nums = [...section.querySelectorAll('.jdgm-paginate__page[data-page]')]
      .map(node => Number(node.getAttribute('data-page')))
      .filter(n => Number.isInteger(n) && n > 0);
    return Math.max(1, ...nums);
  }

  function disconnect(state) {
    state.observer?.disconnect();
    state.observer = null;
    if (state.track && state.onScroll) state.track.removeEventListener('scroll', state.onScroll);
    state.onScroll = null;
  }

  function fallback(state) {
    disconnect(state);
    state.failed = true;
    state.section.classList.remove('bv-judge-carousel-active');
    // Native Judge.me is always left functional, including its page links.
    state.track?.remove();
  }

  function loadPage(section, page) {
    const pager = section.querySelector('.jdgm-widget .jdgm-paginate');
    const link = pager && [...pager.querySelectorAll('.jdgm-paginate__page[data-page]')]
      .find(el => Number(el.dataset.page) === page && !el.classList.contains('jdgm-curt'));
    if (!link) return false;
    link.click();
    return true;
  }

  function waitForJudgeMe(state, previousIds) {
    return new Promise(resolve => {
      let observer, timer, finished = false;
      const finish = nodes => {
        if (finished) return;
        finished = true;
        observer?.disconnect();
        clearTimeout(timer);
        resolve(nodes);
      };
      const check = () => {
        if (!state.section.isConnected || !MOBILE.matches) return finish(null);
        const now = nativeItems(state.section);
        if (now.length && now.some(el => {
          const id = keyOf(el);
          return id && !previousIds.has(id);
        })) finish(now);
      };
      observer = new MutationObserver(check);
      observer.observe(state.section, { childList: true, subtree: true });
      timer = setTimeout(() => finish(null), TIMEOUT);
      check();
    });
  }

  function labelFor(index, total) {
    const lang = document.documentElement.lang || 'en';
    const name = lang.startsWith('ar') ? 'مراجعة' : lang.startsWith('fr') ? 'Avis' : 'Review';
    return name + ' ' + index + ' / ' + total;
  }

  function addCard(state, original) {
    const id = keyOf(original);
    if (!id || state.ids.has(id)) return false;
    const card = original.cloneNode(true);
    // Preserve Judge.me's original markup; do not alter text, names or scores.
    card.setAttribute('role', 'group');
    card.setAttribute('aria-roledescription', 'slide');
    card.setAttribute('aria-label', labelFor(state.ids.size + 1, state.total));
    state.track.appendChild(card);
    state.ids.add(id);
    return true;
  }

  function arm(state) {
    disconnect(state);
    if (!MOBILE.matches || state.failed || !state.section.isConnected ||
        state.nextPage > state.lastPage || !state.track?.isConnected) return;
    const last = state.track.lastElementChild;
    if (!last) return;
    const trigger = () => { if (!state.loading) appendNext(state); };

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) trigger();
      }, { root: state.track, threshold: 0.35 });
      state.observer.observe(last);
    } else {
      state.onScroll = () => {
        const bounds = last.getBoundingClientRect();
        const viewport = state.track.getBoundingClientRect();
        if (bounds.left < viewport.right + 70 && bounds.right > viewport.left - 70) trigger();
      };
      state.track.addEventListener('scroll', state.onScroll, { passive: true });
    }
  }

  async function appendNext(state) {
    if (state.loading || state.failed || !MOBILE.matches ||
        !state.section.isConnected || state.nextPage > state.lastPage) return;
    state.loading = true;
    disconnect(state);
    const before = new Set(state.ids);
    const target = state.nextPage;
    try {
      // Keep the stable track untouched while Judge.me loads its hidden page.
      const pending = waitForJudgeMe(state, before);
      if (!loadPage(state.section, target)) { fallback(state); return; }
      const nextItems = await pending;
      if (!nextItems || !MOBILE.matches || !state.track.isConnected) {
        fallback(state);
        return;
      }
      let appended = 0;
      nextItems.forEach(item => { if (addCard(state, item)) appended++; });
      if (!appended) { fallback(state); return; }
      state.nextPage++;
      // No replaceChildren(), no scrollLeft reset, no interaction with visible cards.
      if (state.nextPage <= state.lastPage) arm(state);
      else state.section.classList.add('bv-local-review-all-loaded');
    } catch (_) {
      fallback(state);
    } finally {
      state.loading = false;
    }
  }

  function init(section) {
    if (!MOBILE.matches || !section.isConnected) return;
    const already = mounted.get(section);
    if (already && !already.failed) {
      if (!already.track.isConnected) { fallback(already); return; }
      section.classList.add('bv-judge-carousel-active');
      if (!already.loading && already.nextPage <= already.lastPage && !already.observer && !already.onScroll) arm(already);
      return;
    }
    const widget = section.querySelector('.jdgm-widget.jdgm-review-widget');
    const pager = widget?.querySelector('.jdgm-paginate');
    const first = nativeItems(section);
    if (!widget || !pager || !first.length) return;

    const ids = first.map(keyOf);
    if (ids.some(id => !id) || new Set(ids).size !== ids.length) return;
    const lastPage = maxPage(section);
    if (lastPage > MAX_PAGES) return;

    const track = document.createElement('div');
    track.className = 'bv-judge-stable-track';
    track.setAttribute('role', 'region');
    track.setAttribute('aria-roledescription', 'carousel');
    track.setAttribute('aria-label',
      (document.documentElement.lang || '').startsWith('ar') ? 'آراء الزبناء' :
      (document.documentElement.lang || '').startsWith('fr') ? 'Avis clients' : 'Customer reviews'
    );
    // Keep the native widget's Ajax subtree separate from the visible carousel.
    widget.insertAdjacentElement('afterend', track);

    const summaryCount = Number(widget.querySelector('.jdgm-rev-widg')?.getAttribute('data-number-of-reviews'));
    const total = Number.isFinite(summaryCount) && summaryCount > 0 ? summaryCount : first.length;
    const state = {
      section, track, ids:new Set(), total, nextPage:2, lastPage,
      observer:null, onScroll:null, failed:false, loading:false
    };
    first.forEach(card => addCard(state, card));
    mounted.set(section, state);
    section.classList.add('bv-judge-carousel-active');
    if (lastPage > 1) arm(state);
    else section.classList.add('bv-local-review-all-loaded');
  }

  function scan(root = document) {
    if (root.matches?.(SECTION)) init(root);
    root.querySelectorAll?.(SECTION).forEach(init);
  }

  function modeChanged() {
    document.querySelectorAll(SECTION).forEach(section => {
      const state = mounted.get(section);
      if (!MOBILE.matches && state) {
        disconnect(state);
        section.classList.remove('bv-judge-carousel-active');
      } else if (MOBILE.matches) init(section);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  MOBILE.addEventListener?.('change', modeChanged);
})();
